// Persisted "Areas of the business" scope reader/writer (spec 023 FR-009, SC-008;
// 027 Slice B reader migration FR-009/FR-011, SC-004/SC-005).
//
// SOURCE OF TRUTH (027 Slice B): when the EngagementProvider has resolved a
// current engagement, `readScope()` reads over the provider's module-level
// SNAPSHOT — specifically the `streams` sub-doc. It stays an ADAPTER (recorded
// Slice B choice — smallest blast radius): it KEEPS returning { areas: [...] }
// mapped from `snapshot.streams.streams` (the same { id, name, oneLiner?, smes }
// field set), so consumers (App.tsx:57, ScopeWidget) are untouched this slice;
// the visible 'areas'→'streams' rename is a later deliberate increment. When
// there is NO snapshot (null — first-run / safe state / no provider mounted yet)
// it falls back to the legacy localStorage["arcpilot.scope"] home so the read
// shape is preserved exactly. Defensive `readStored`-style util: jsdom/SSR-safe
// and migration-tolerant — a missing / partial / garbage source NEVER throws and
// yields a safe empty shape ({ areas: [] }).

import { getEngagementSnapshot } from '../hooks/useEngagement';

export const SCOPE_KEY = 'arcpilot.scope';

export interface SME {
  name: string;
  /** Email or role. */
  contact?: string;
}

export interface Area {
  id: string;
  name: string;
  oneLiner?: string;
  smes: SME[];
}

export interface Scope {
  areas: Area[];
}

function emptyScope(): Scope {
  return { areas: [] };
}

// Coerce an already-deserialised record that carries an `areas` array
// (localStorage's { areas } shape) to the safe Scope shape.
function parseScopeRecord(parsed: unknown): Scope {
  if (!parsed || typeof parsed !== 'object') return emptyScope();
  const record = parsed as Partial<Scope>;
  return {
    areas: Array.isArray(record.areas) ? (record.areas as Area[]) : [],
  };
}

// Adapt the engagement snapshot's `streams` sub-doc ({ streams: Area[] }) to the
// { areas } shape the consumers still expect this slice.
function parseStreamsSubDoc(parsed: unknown): Scope {
  if (!parsed || typeof parsed !== 'object') return emptyScope();
  const record = parsed as { streams?: unknown };
  return {
    areas: Array.isArray(record.streams) ? (record.streams as Area[]) : [],
  };
}

export function readScope(): Scope {
  // 027 Slice B: prefer the engagement snapshot's `streams` sub-doc when present
  // (mapped to { areas } by the adapter); else fall back to the legacy
  // localStorage home (preserving the pre-migration read shape).
  const snapshot = getEngagementSnapshot();
  if (snapshot) {
    try {
      return parseStreamsSubDoc(snapshot.streams);
    } catch {
      return emptyScope();
    }
  }
  try {
    const raw = window.localStorage.getItem(SCOPE_KEY);
    if (!raw) return emptyScope();
    return parseScopeRecord(JSON.parse(raw) as unknown);
  } catch {
    return emptyScope();
  }
}

export function writeScope(scope: Scope): void {
  try {
    window.localStorage.setItem(SCOPE_KEY, JSON.stringify(scope));
  } catch {
    // jsdom/SSR-safe: storage unavailable → no-op, never throws.
  }
}
